import { useCallback, useRef, useState } from "react";

import { DataEditorRef } from "@glideapps/glide-data-grid";
import { InputRef } from "antd";
import { useAtomValue, useSetAtom } from "jotai";

import { ColumnData, columnsAtom } from "../atoms/fieldGroupAtoms";

/**
 * Validate new column name and return the new column if valid
 */
export function validateNewColumn(
  columns: ColumnData[],
  name: string,
  defaultUnit?: string
): { isValid: boolean; newColumn?: ColumnData } {
  if (columns.some((c) => c.name === name)) {
    return { isValid: false };
  }

  return {
    isValid: true,
    newColumn: {
      name,
      propertyName: name,
      data: {},
      isNew: true,
      defaultUnit: defaultUnit?.trim() || undefined
    }
  };
}

/**
 * Add a new column to the columns array
 */
export function addColumnToArray(
  columns: ColumnData[],
  newColumn: ColumnData
): ColumnData[] {
  return [...columns, newColumn];
}

/**
 * Remove a column from the columns array by name
 */
export function removeColumnFromArray(columns: ColumnData[], name: string): ColumnData[] {
  return columns.filter((col) => col.name !== name);
}

/**
 * Hook for managing column add/remove operations
 */
export function useColumnManagement(gridRef?: React.RefObject<DataEditorRef>) {
  const columns = useAtomValue(columnsAtom);
  const setColumns = useSetAtom(columnsAtom);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [columnName, setColumnName] = useState("");
  const [columnUnit, setColumnUnit] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [menuColumn, setMenuColumn] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const inputRef = useRef<InputRef>(null);

  const handleAddColumn = useCallback(() => {
    const trimmedName = columnName.trim();

    if (!trimmedName) {
      setError("Column name is required");
      return;
    }

    const { isValid, newColumn } = validateNewColumn(columns, trimmedName, columnUnit);

    if (!isValid) {
      setError(`Column "${trimmedName}" already exists in this group`);
      return;
    }

    if (newColumn) {
      const newColumns = addColumnToArray(columns, newColumn);
      setColumns(newColumns);

      // Wait for the grid to render the new column before scrolling
      // Use setTimeout to allow Jotai state propagation and grid re-render
      window.setTimeout(() => {
        if (gridRef?.current) {
          // Scroll to the last column index
          const lastColIndex = newColumns.length - 1;
          gridRef.current.scrollTo(lastColIndex, 0, "horizontal", 0, 0, {
            hAlign: "start",
            vAlign: "start"
          });
        }
      }, 150);
    }

    setError(null);
    setColumnName("");
    setColumnUnit("");
    setIsPopoverOpen(false);
  }, [columnName, columnUnit, columns, setColumns, gridRef]);

  const handleRemoveColumn = useCallback(
    (columnName: string) => {
      setColumns(removeColumnFromArray(columns, columnName));
      setMenuColumn(null);
      setMenuPosition(null);
    },
    [columns, setColumns]
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleAddColumn();
      }
    },
    [handleAddColumn]
  );

  const handleColumnNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setColumnName(e.target.value);
      if (error) setError(null);
    },
    [error]
  );

  const handleColumnUnitChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setColumnUnit(e.target.value);
  }, []);

  const handlePopoverOpenChange = useCallback((isOpen: boolean) => {
    setIsPopoverOpen(isOpen);
    if (isOpen) {
      setError(null);
      setColumnName("");
      setColumnUnit("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, []);

  const handleCancel = useCallback(() => {
    setError(null);
    setColumnName("");
    setColumnUnit("");
    setIsPopoverOpen(false);
  }, []);

  const showContextMenu = useCallback((col: number, x: number, y: number) => {
    setMenuColumn(col);
    setMenuPosition({ x, y });
  }, []);

  const hideContextMenu = useCallback(() => {
    setMenuColumn(null);
    setMenuPosition(null);
  }, []);

  return {
    // State
    isPopoverOpen,
    columnName,
    columnUnit,
    error,
    menuColumn,
    menuPosition,
    inputRef,
    // Handlers
    handleAddColumn,
    handleRemoveColumn,
    handleKeyPress,
    handleColumnNameChange,
    handleColumnUnitChange,
    handlePopoverOpenChange,
    handleCancel,
    showContextMenu,
    hideContextMenu
  };
}
